import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const CanonicalTextSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^(?:\\S|\\S[\\s\\S]*\\S)$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NonNegativeQuantitySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveVersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NonNegativeVersionSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });

export const INVENTORY_LOT_NEAR_EXPIRY_DAYS = 30;

export const InventoryLotReceiptAllocationRequestSchema = Type.Object(
  {
    lotId: StableIdSchema,
    lotNumber: CanonicalTextSchema,
    expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    receivedBaseQuantity: PositiveQuantitySchema,
  },
  { additionalProperties: false },
);
export type InventoryLotReceiptAllocationRequest = Static<
  typeof InventoryLotReceiptAllocationRequestSchema
>;

export const InventoryLotReceiptAllocationRecordSchema = Type.Object(
  {
    ...InventoryLotReceiptAllocationRequestSchema.properties,
    quantityBefore: NonNegativeQuantitySchema,
    quantityAfter: PositiveQuantitySchema,
    versionBefore: NonNegativeVersionSchema,
    versionAfter: PositiveVersionSchema,
  },
  { additionalProperties: false },
);
export type InventoryLotReceiptAllocationRecord = Static<
  typeof InventoryLotReceiptAllocationRecordSchema
>;

export const InventoryLotReservationAllocationSchema = Type.Object(
  {
    lotId: StableIdSchema,
    lotNumber: CanonicalTextSchema,
    expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    reservedBaseQuantity: PositiveQuantitySchema,
  },
  { additionalProperties: false },
);
export type InventoryLotReservationAllocation = Static<
  typeof InventoryLotReservationAllocationSchema
>;

export const InventoryLotSaleAllocationSchema = Type.Object(
  {
    lotId: StableIdSchema,
    lotNumber: CanonicalTextSchema,
    expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    soldBaseQuantity: PositiveQuantitySchema,
    quantityBefore: PositiveQuantitySchema,
    quantityAfter: NonNegativeQuantitySchema,
    versionBefore: PositiveVersionSchema,
    versionAfter: PositiveVersionSchema,
  },
  { additionalProperties: false },
);
export type InventoryLotSaleAllocation = Static<typeof InventoryLotSaleAllocationSchema>;

export const InventoryLotReturnAllocationSchema = Type.Object(
  {
    lotId: StableIdSchema,
    lotNumber: CanonicalTextSchema,
    expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    returnedBaseQuantity: PositiveQuantitySchema,
    quantityBefore: NonNegativeQuantitySchema,
    quantityAfter: PositiveQuantitySchema,
    versionBefore: PositiveVersionSchema,
    versionAfter: PositiveVersionSchema,
  },
  { additionalProperties: false },
);
export type InventoryLotReturnAllocation = Static<typeof InventoryLotReturnAllocationSchema>;

export const InventoryLotAvailabilityStatusSchema = Type.Union([
  Type.Literal("AVAILABLE"),
  Type.Literal("NEAR_EXPIRY"),
  Type.Literal("EXPIRED"),
  Type.Literal("EMPTY"),
]);
export type InventoryLotAvailabilityStatus = Static<typeof InventoryLotAvailabilityStatusSchema>;

export const InventoryLotReadModelSchema = Type.Object(
  {
    lotId: StableIdSchema,
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 120 }),
    baseUnitName: Type.String({ minLength: 1, maxLength: 32 }),
    lotNumber: CanonicalTextSchema,
    expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    quantity: NonNegativeQuantitySchema,
    reservedQuantity: NonNegativeQuantitySchema,
    availableQuantity: NonNegativeQuantitySchema,
    version: PositiveVersionSchema,
    sourceReceiptId: Type.Union([StableIdSchema, Type.Null()]),
    sourceReceiptNumber: Type.Union([
      Type.String({ pattern: "^SH-[0-9]{8}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
    receivedAt: TimestampSchema,
    status: InventoryLotAvailabilityStatusSchema,
  },
  { additionalProperties: false },
);
export type InventoryLotReadModel = Static<typeof InventoryLotReadModelSchema>;

export const InventoryLotWorkspaceSchema = Type.Object(
  {
    asOfBusinessDate: BusinessDateSchema,
    nearExpiryDays: Type.Literal(INVENTORY_LOT_NEAR_EXPIRY_DAYS),
    items: Type.Array(InventoryLotReadModelSchema, { maxItems: 5_000 }),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type InventoryLotWorkspace = Static<typeof InventoryLotWorkspaceSchema>;

export interface InventoryLotFefoCandidate {
  lotId: string;
  lotNumber: string;
  expiryDate: string | null;
  quantity: number;
  reservedQuantity: number;
  version: number;
  receivedAt: string;
}

function canonicalBusinessDate(value: string): boolean {
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(timestamp) && new Date(timestamp).toISOString() === `${value}T00:00:00.000Z`
  );
}

function canonicalTimestamp(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function inventoryLotStatus(
  quantity: number,
  expiryDate: string | null,
  asOfBusinessDate: string,
): InventoryLotAvailabilityStatus {
  if (!Number.isSafeInteger(quantity) || quantity < 0 || !canonicalBusinessDate(asOfBusinessDate)) {
    throw new RangeError("inventory lot status inputs are invalid");
  }
  if (quantity === 0) return "EMPTY";
  if (expiryDate === null) return "AVAILABLE";
  if (!canonicalBusinessDate(expiryDate)) {
    throw new RangeError("inventory lot expiry date is invalid");
  }
  if (expiryDate < asOfBusinessDate) return "EXPIRED";
  const expiryTimestamp = Date.parse(`${expiryDate}T00:00:00.000Z`);
  const asOfTimestamp = Date.parse(`${asOfBusinessDate}T00:00:00.000Z`);
  return expiryTimestamp - asOfTimestamp <= INVENTORY_LOT_NEAR_EXPIRY_DAYS * 86_400_000
    ? "NEAR_EXPIRY"
    : "AVAILABLE";
}

export function allocateInventoryLotsFefo(
  candidates: readonly InventoryLotFefoCandidate[],
  requestedQuantity: number,
  asOfBusinessDate: string,
): InventoryLotSaleAllocation[] {
  if (
    !Number.isSafeInteger(requestedQuantity) ||
    requestedQuantity < 1 ||
    !canonicalBusinessDate(asOfBusinessDate)
  ) {
    throw new RangeError("inventory lot FEFO inputs are invalid");
  }
  const uniqueIds = new Set<string>();
  const eligible = candidates
    .map((candidate) => {
      if (
        uniqueIds.has(candidate.lotId) ||
        !Value.Check(
          Type.Object(
            {
              lotId: StableIdSchema,
              lotNumber: CanonicalTextSchema,
              expiryDate: Type.Union([BusinessDateSchema, Type.Null()]),
              quantity: NonNegativeQuantitySchema,
              reservedQuantity: NonNegativeQuantitySchema,
              version: PositiveVersionSchema,
              receivedAt: TimestampSchema,
            },
            { additionalProperties: false },
          ),
          candidate,
        ) ||
        candidate.reservedQuantity > candidate.quantity ||
        !canonicalTimestamp(candidate.receivedAt)
      ) {
        throw new RangeError("inventory lot FEFO candidate is invalid");
      }
      uniqueIds.add(candidate.lotId);
      return candidate;
    })
    .filter(
      (candidate) =>
        candidate.quantity > candidate.reservedQuantity &&
        (candidate.expiryDate === null || candidate.expiryDate >= asOfBusinessDate),
    )
    .sort((left, right) => {
      if (left.expiryDate === null && right.expiryDate !== null) return 1;
      if (left.expiryDate !== null && right.expiryDate === null) return -1;
      if (left.expiryDate !== right.expiryDate) {
        return (left.expiryDate ?? "").localeCompare(right.expiryDate ?? "");
      }
      if (left.receivedAt !== right.receivedAt)
        return left.receivedAt.localeCompare(right.receivedAt);
      return left.lotId.localeCompare(right.lotId);
    });
  const available = safeSum(
    eligible.map((candidate) => candidate.quantity - candidate.reservedQuantity),
  );
  if (available === null || available < requestedQuantity) {
    throw new RangeError("inventory lot saleable quantity is insufficient");
  }
  const allocations: InventoryLotSaleAllocation[] = [];
  let remaining = requestedQuantity;
  for (const candidate of eligible) {
    if (remaining === 0) break;
    const soldBaseQuantity = Math.min(remaining, candidate.quantity - candidate.reservedQuantity);
    if (soldBaseQuantity === 0) continue;
    allocations.push({
      lotId: candidate.lotId,
      lotNumber: candidate.lotNumber,
      expiryDate: candidate.expiryDate,
      soldBaseQuantity,
      quantityBefore: candidate.quantity,
      quantityAfter: candidate.quantity - soldBaseQuantity,
      versionBefore: candidate.version,
      versionAfter: candidate.version + 1,
    });
    remaining -= soldBaseQuantity;
  }
  return allocations;
}

export function allocateInventoryLotReturn(
  originalAllocations: readonly InventoryLotSaleAllocation[],
  returnedQuantityBefore: number,
  returnedQuantityAfter: number,
  currentLots: ReadonlyMap<string, { quantity: number; version: number }>,
): InventoryLotReturnAllocation[] {
  const soldQuantity = safeSum(
    originalAllocations.map((allocation) => allocation.soldBaseQuantity),
  );
  if (
    soldQuantity === null ||
    !Number.isSafeInteger(returnedQuantityBefore) ||
    !Number.isSafeInteger(returnedQuantityAfter) ||
    returnedQuantityBefore < 0 ||
    returnedQuantityAfter <= returnedQuantityBefore ||
    returnedQuantityAfter > soldQuantity ||
    new Set(originalAllocations.map((allocation) => allocation.lotId)).size !==
      originalAllocations.length
  ) {
    throw new RangeError("inventory lot return inputs are invalid");
  }
  const allocations: InventoryLotReturnAllocation[] = [];
  let cursor = 0;
  for (const original of originalAllocations) {
    if (!Value.Check(InventoryLotSaleAllocationSchema, original)) {
      throw new RangeError("inventory lot original sale allocation is invalid");
    }
    const segmentStart = cursor;
    const segmentEnd = cursor + original.soldBaseQuantity;
    cursor = segmentEnd;
    const overlapStart = Math.max(segmentStart, returnedQuantityBefore);
    const overlapEnd = Math.min(segmentEnd, returnedQuantityAfter);
    const returnedBaseQuantity = Math.max(0, overlapEnd - overlapStart);
    if (returnedBaseQuantity === 0) continue;
    const current = currentLots.get(original.lotId);
    if (
      !current ||
      !Number.isSafeInteger(current.quantity) ||
      current.quantity < 0 ||
      !Number.isSafeInteger(current.version) ||
      current.version < 1 ||
      !Number.isSafeInteger(current.quantity + returnedBaseQuantity)
    ) {
      throw new RangeError("inventory lot return current state is invalid");
    }
    allocations.push({
      lotId: original.lotId,
      lotNumber: original.lotNumber,
      expiryDate: original.expiryDate,
      returnedBaseQuantity,
      quantityBefore: current.quantity,
      quantityAfter: current.quantity + returnedBaseQuantity,
      versionBefore: current.version,
      versionAfter: current.version + 1,
    });
  }
  return allocations;
}

export function isInventoryLotReceiptAllocationRecord(
  value: unknown,
): value is InventoryLotReceiptAllocationRecord {
  return (
    Value.Check(InventoryLotReceiptAllocationRecordSchema, value) &&
    value.quantityAfter === value.quantityBefore + value.receivedBaseQuantity &&
    value.versionAfter === value.versionBefore + 1
  );
}

export function isInventoryLotSaleAllocation(value: unknown): value is InventoryLotSaleAllocation {
  return (
    Value.Check(InventoryLotSaleAllocationSchema, value) &&
    value.quantityAfter === value.quantityBefore - value.soldBaseQuantity &&
    value.versionAfter === value.versionBefore + 1
  );
}

export function isInventoryLotReturnAllocation(
  value: unknown,
): value is InventoryLotReturnAllocation {
  return (
    Value.Check(InventoryLotReturnAllocationSchema, value) &&
    value.quantityAfter === value.quantityBefore + value.returnedBaseQuantity &&
    value.versionAfter === value.versionBefore + 1
  );
}

export function isInventoryLotWorkspace(value: unknown): value is InventoryLotWorkspace {
  if (!Value.Check(InventoryLotWorkspaceSchema, value)) return false;
  if (!canonicalBusinessDate(value.asOfBusinessDate) || !canonicalTimestamp(value.generatedAt)) {
    return false;
  }
  const lotIds = new Set<string>();
  return value.items.every((item) => {
    if (lotIds.has(item.lotId) || !canonicalTimestamp(item.receivedAt)) return false;
    lotIds.add(item.lotId);
    return (
      item.reservedQuantity <= item.quantity &&
      item.availableQuantity ===
        (item.expiryDate !== null && item.expiryDate < value.asOfBusinessDate
          ? 0
          : item.quantity - item.reservedQuantity) &&
      item.status === inventoryLotStatus(item.quantity, item.expiryDate, value.asOfBusinessDate)
    );
  });
}
